// AuthProvider.jsx
import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { 
  CognitoUserPool, 
  CognitoUser, 
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession
} from 'amazon-cognito-identity-js';
import { poolData, SESSION_CONFIG, AUTH_ERRORS } from './authConfig';
import { getTemporaryCredentials } from '../../utils/awsAuth';
import { jwtDecode } from 'jwt-decode';
import { BedrockRuntimeClient } from '@aws-sdk/client-bedrock-runtime';
import { DynamoDBClient } from '@aws-sdk/client-dynamodb';
import { S3Client } from '@aws-sdk/client-s3';

const AuthContext = createContext();

// Create a global reference that can be accessed from non-component code
if (typeof window !== 'undefined') {
  window._authContextRef = { current: null };
}

// Export isTokenExpiring for other modules to use
export function isTokenExpiring(token, bufferTime = 0) {
  if (!token) return true;
  
  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    return decoded.exp < currentTime + bufferTime / 1000;
  } catch (err) {
    console.error('Error checking token expiration:', err);
    return true;
  }
}

// Memory storage for tokens when not using localStorage
const memoryTokenStorage = {
  accessToken: null,
  idToken: null,
  refreshToken: null
};

export const AuthProvider = ({ children }) => {
  // Use refs for token storage to prevent unnecessary re-renders
  const tokensRef = useRef({
    accessToken: null,
    idToken: null,
    refreshToken: null
  });
  
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tokenValidationComplete, setTokenValidationComplete] = useState(false);
  
  // Session management
  const [sessionExpiry, setSessionExpiry] = useState(null);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [keepMeLoggedIn, setKeepMeLoggedIn] = useState(false);
  
  // Add AWS Ready state to track proper initialization
  const [awsReady, setAwsReady] = useState(false);
  
  // Refs for AWS clients
  const bedrockRuntimeClientRef = useRef(null);
  const dynamoDBClientRef = useRef(null);
  const s3ClientRef = useRef(null);
  
  // Activity tracking for session timeout
  const lastActivityRef = useRef(Date.now());
  const refreshTimerRef = useRef(null);
  const inactivityTimerRef = useRef(null);
  const sessionWarningTimerRef = useRef(null);
  
  // Retry counter for network operations
  const retryCounterRef = useRef(0);
  
  // User pool initialization
  const userPool = new CognitoUserPool(poolData);

  // Helper function to store tokens securely based on configuration - updated for clarity
  const storeTokens = useCallback((tokens) => {
    if (!tokens || !tokens.idToken || !tokens.accessToken) {
      console.warn('Attempting to store invalid tokens:', tokens);
      return;
    }

    console.log('Storing tokens with method:', SESSION_CONFIG.storageType);
    
    // Update tokens ref first
    tokensRef.current = { ...tokens };
    
    if (SESSION_CONFIG.storageType === 'localStorage' || keepMeLoggedIn) {
      console.log('Storing tokens in localStorage');
      localStorage.setItem('accessToken', tokens.accessToken);
      localStorage.setItem('idToken', tokens.idToken);
      localStorage.setItem('refreshToken', tokens.refreshToken || '');
    } 
    
    if (SESSION_CONFIG.storageType === 'sessionStorage' && !keepMeLoggedIn) {
      console.log('Storing tokens in sessionStorage');
      sessionStorage.setItem('accessToken', tokens.accessToken);
      sessionStorage.setItem('idToken', tokens.idToken);
      sessionStorage.setItem('refreshToken', tokens.refreshToken || '');
    } 
    
    // Always update memory storage as a fallback
    console.log('Updating memory token storage');
    memoryTokenStorage.accessToken = tokens.accessToken;
    memoryTokenStorage.idToken = tokens.idToken;
    memoryTokenStorage.refreshToken = tokens.refreshToken;
  }, [keepMeLoggedIn]);

  // Helper function to retrieve tokens - updated to be more robust
  const getStoredTokens = useCallback(() => {
    // Return tokens from ref first if they exist
    if (tokensRef.current.idToken && tokensRef.current.accessToken) {
      console.log('Using tokens from memory reference');
      return { ...tokensRef.current };
    }
    
    // Otherwise check storage locations
    if (SESSION_CONFIG.storageType === 'localStorage' || keepMeLoggedIn) {
      const tokens = {
        accessToken: localStorage.getItem('accessToken'),
        idToken: localStorage.getItem('idToken'),
        refreshToken: localStorage.getItem('refreshToken')
      };
      
      // Update token refs if found in storage
      if (tokens.accessToken && tokens.idToken) {
        tokensRef.current = { ...tokens };
        console.log('Retrieved tokens from localStorage');
        return tokens;
      }
    }
    
    // Try sessionStorage if we didn't find in localStorage
    const sessionTokens = {
      accessToken: sessionStorage.getItem('accessToken'),
      idToken: sessionStorage.getItem('idToken'),
      refreshToken: sessionStorage.getItem('refreshToken')
    };
    
    // Update token refs if found in session storage
    if (sessionTokens.accessToken && sessionTokens.idToken) {
      tokensRef.current = { ...sessionTokens };
      console.log('Retrieved tokens from sessionStorage');
      return sessionTokens;
    }
    
    // Try memory storage as last resort
    if (memoryTokenStorage.accessToken && memoryTokenStorage.idToken) {
      console.log('Retrieved tokens from memory storage');
      return { ...memoryTokenStorage };
    }
    
    console.log('No tokens found in any storage');
    return {
      accessToken: null,
      idToken: null,
      refreshToken: null
    };
  }, [keepMeLoggedIn]);

  // Clear all stored tokens
  const clearTokens = useCallback(() => {
    // Clear token refs first
    tokensRef.current = {
      accessToken: null,
      idToken: null,
      refreshToken: null
    };
    
    localStorage.removeItem('accessToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('refreshToken');
    
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('idToken');
    sessionStorage.removeItem('refreshToken');
    
    memoryTokenStorage.accessToken = null;
    memoryTokenStorage.idToken = null;
    memoryTokenStorage.refreshToken = null;
  }, []);

  // Extract user information from token
  const extractUserFromToken = (idToken) => {
    try {
      if (!idToken) return null;
      
      const decodedToken = jwtDecode(idToken);
      return {
        sub: decodedToken.sub,
        email: decodedToken.email,
        email_verified: decodedToken.email_verified,
        exp: decodedToken.exp,
        tokens: {
          idToken,
          accessToken: tokensRef.current.accessToken,
          refreshToken: tokensRef.current.refreshToken
        }
      };
    } catch (err) {
      console.error('Error decoding token:', err);
      return null;
    }
  };

  // Initialize AWS clients with current credentials
  const initAwsClients = useCallback(async (forcedIdToken) => {
    try {
      // 1) Load tokens from storage
      const tokens = getStoredTokens();
      const idToken = forcedIdToken ?? tokens?.idToken;
      
      // If no token, bail out
      if (!isAuthenticated && !idToken) {
        console.log('Not initializing AWS clients - no authentication detected');
        return false;
      }
      
      console.log('Initializing AWS clients with credentials');

      // 2) Pass the token to avoid any race conditions
      const credentials = await getTemporaryCredentials({ idToken });
      if (!credentials) {
        console.error('Failed to get temporary credentials');
        return false;
      }
      
      // Initialize Bedrock client
      bedrockRuntimeClientRef.current = new BedrockRuntimeClient({
        credentials,
        region: process.env.REACT_APP_AWS_REGION || 'us-west-2'
      });

      // Initialize DynamoDB client
      dynamoDBClientRef.current = new DynamoDBClient({
        credentials,
        region: process.env.REACT_APP_AWS_REGION || 'us-west-2'
      });

      // Initialize S3 client
      s3ClientRef.current = new S3Client({
        credentials,
        region: process.env.REACT_APP_AWS_REGION || 'us-west-2'
      });
      
      console.log('AWS clients initialized successfully');
      setAwsReady(true); // Mark AWS initialization as complete
      return true;
    } catch (err) {
      console.error('Error initializing AWS clients:', err);
      
      // Check for common authentication errors
      if (err.message?.includes('NotAuthorizedException') || 
          err.message?.includes('Unauthenticated access is not supported')) {
        // This suggests there might be an issue with the token or identity pool configuration
        console.warn('Authentication error detected. Please try logging in again.');
        // Don't call refreshTokens here to avoid circular dependency
        setError(AUTH_ERRORS.authenticationError || 'Authentication failed');
      } else {
        setError(AUTH_ERRORS.generalError);
      }
      return false;
    }
  }, [isAuthenticated, getStoredTokens]);

  // Get active Bedrock client or initialize if needed
  const getBedrockRuntimeClient = useCallback(async () => {
    if (!bedrockRuntimeClientRef.current) {
      await initAwsClients();
    }
    return bedrockRuntimeClientRef.current;
  }, [initAwsClients]);

  // Get active DynamoDB client or initialize if needed
  const getDynamoDBClient = useCallback(async () => {
    if (!dynamoDBClientRef.current) {
      await initAwsClients();
    }
    return dynamoDBClientRef.current;
  }, [initAwsClients]);

  // Get active S3 client or initialize if needed
  const getS3Client = useCallback(async () => {
    if (!s3ClientRef.current) {
      await initAwsClients();
    }
    return s3ClientRef.current;
  }, [initAwsClients]);

  // Near the top of the file, add this new ref for safe logout
  const logoutRef = useRef(null);
  
  // Define the logout function early and store a reference to avoid circular dependencies
  const logout = useCallback(() => {
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      currentUser.signOut();
    }
    
    // Clear tokens and user state
    clearTokens();
    setUser(null);
    setIsAuthenticated(false);
    setSessionExpiry(null);
    setShowSessionWarning(false);
    setAwsReady(false); // Reset AWS Ready state
    
    // Clear all timers
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (sessionWarningTimerRef.current) clearTimeout(sessionWarningTimerRef.current);
  }, [clearTokens]);
  
  // Store the logout function in the ref to make it safely available to other functions
  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  // Refresh the session tokens using the Cognito refresh token
  const refreshTokens = useCallback(async (options = {}) => {
    const source = options.source || 'unknown';
    console.log(`🔄 Attempting to refresh tokens... [Source: ${source}]`);
    
    // Don't refresh if we're actively trying, to prevent infinite loops
    const isRefreshing = options.isRefreshing || false;
    const forceRefresh = options.forceRefresh || false;
    
    // NEW: Track last refresh time to prevent rapid successive refreshes
    const now = Date.now();
    const lastRefreshTime = refreshTokens.lastRefreshTime || 0;
    const minRefreshInterval = 30000; // 30 seconds minimum between refreshes
    
    if (now - lastRefreshTime < minRefreshInterval && !forceRefresh) {
      console.log(`[DEBUG REFRESH] Skipping refresh - last refresh was ${Math.round((now - lastRefreshTime)/1000)}s ago [Source: ${source}]`);
      // If we already have tokens, return them rather than refreshing again
      const tokens = getStoredTokens();
      if (tokens.idToken && tokens.accessToken) {
        return tokens;
      }
      return null;
    }
    
    console.log(`[DEBUG REFRESH] Proceeding with token refresh [Source: ${source}, ForceRefresh: ${forceRefresh}, TimeSinceLastRefresh: ${Math.round((now - lastRefreshTime)/1000)}s]`);
    
    // Update the last refresh time
    refreshTokens.lastRefreshTime = now;
    
    if (isRefreshing && retryCounterRef.current > 0) {
      console.log('[DEBUG REFRESH] Already refreshing tokens, skipping duplicate request');
      return null;
    }
    
    const currentUser = userPool.getCurrentUser();
    if (!currentUser) {
      console.error('No current user found for token refresh');
      throw new Error('No current user found');
    }

    return new Promise((resolve, reject) => {
      currentUser.getSession((err, session) => {
        if (err) {
          console.error('Error refreshing session:', err);
          
          // Special handling for invalid refresh token errors
          if (err.message?.includes('Invalid Refresh Token') || 
              err.message?.includes('NotAuthorizedException')) {
            console.warn('Invalid refresh token detected during refresh - user needs to re-login');
            // Use the safe reference to logout instead of direct function call
            setTimeout(() => {
              if (logoutRef.current) {
                logoutRef.current();
              }
            }, 0);
            reject(err);
            return;
          }
          
          reject(err);
          return;
        }
        
        if (session && session.isValid()) {
          console.log('✅ Successfully refreshed session tokens');
          
          const tokens = {
            accessToken: session.getAccessToken().getJwtToken(),
            idToken: session.getIdToken().getJwtToken(),
            refreshToken: session.getRefreshToken().getToken()
          };
          
          // Store tokens using our improved function
          storeTokens(tokens);
          
          // Update session expiry
          const decodedToken = jwtDecode(tokens.idToken);
          const expiryTime = decodedToken.exp * 1000; // Convert to milliseconds
          setSessionExpiry(expiryTime);
          
          // Reset session warning state if we successfully refreshed tokens
          if (showSessionWarning) {
            setShowSessionWarning(false);
          }
          
          // Reset retry counter on successful refresh
          retryCounterRef.current = 0;
          
          // Update user info with fresh tokens
          const userData = extractUserFromToken(tokens.idToken);
          setUser(userData);
          
          // Reinitialize AWS clients with new credentials
          // We don't want to add initAwsClients to the dependency array to avoid circular dependencies
          setTimeout(() => {
            // NEW: Only initialize AWS clients if they don't exist or if forced
            const shouldInitClients = !bedrockRuntimeClientRef.current || 
                                      !dynamoDBClientRef.current || 
                                      !s3ClientRef.current ||
                                      options.forceRefresh;
            
            if (shouldInitClients) {
              initAwsClients(tokens.idToken)
                .then(() => {
                  // Dispatch an event that other components can listen to
                  const event = new CustomEvent('auth:tokens-refreshed', { 
                    detail: { 
                      timestamp: Date.now(),
                      userId: userData?.sub
                    }
                  });
                  window.dispatchEvent(event);
                  console.log('Dispatched tokens-refreshed event');
                })
                .catch(err => console.error('Failed to initialize AWS clients after token refresh:', err));
            } else {
              console.log('Skipping AWS client initialization - clients already exist');
            }
          }, 0);
          
          resolve(tokens);
        } else {
          console.error('Refreshed session is invalid');
          reject(new Error('Session is invalid'));
        }
      });
    });
  }, [storeTokens, showSessionWarning]); // Remove logout from dependency array

  // Handle user login
  const login = useCallback(async (username, password, rememberMe = false) => {
    setLoading(true);
    setError(null);
    setKeepMeLoggedIn(rememberMe);

    try {
      const authenticationDetails = new AuthenticationDetails({
        Username: username,
        Password: password,
      });

      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool
      });

      return new Promise((resolve, reject) => {
        cognitoUser.authenticateUser(authenticationDetails, {
          onSuccess: async (session) => {
            const tokens = {
              accessToken: session.getAccessToken().getJwtToken(),
              idToken: session.getIdToken().getJwtToken(),
              refreshToken: session.getRefreshToken().getToken()
            };
            
            storeTokens(tokens);
            
            const userData = extractUserFromToken(tokens.idToken);
            
            // Set all state first and wait for a tick to ensure React has processed the state updates
            setUser(userData);
            setIsAuthenticated(true);
            
            // Set session expiry
            const decodedToken = jwtDecode(tokens.idToken);
            const expiryTime = decodedToken.exp * 1000; // Convert to milliseconds
            setSessionExpiry(expiryTime);
            
            // Use setTimeout to ensure React state has been updated before initializing clients
            setTimeout(async () => {
              try {
                // Initialize AWS clients after the state has been updated
                await initAwsClients();
                setLoading(false);
                resolve({ success: true });
              } catch (error) {
                console.error("Error initializing AWS clients after login:", error);
                // Still consider login successful even if client init fails
                setLoading(false);
                resolve({ success: true, clientInitError: true });
              }
            }, 0);
          },
          onFailure: (err) => {
            console.error('Login error:', err);
            
            let errorMessage = AUTH_ERRORS.generalError;
            if (err.message === 'User does not exist.') {
              errorMessage = AUTH_ERRORS.userNotFound;
            } else if (err.message === 'Incorrect username or password.') {
              errorMessage = AUTH_ERRORS.invalidCredentials;
            } else if (err.message.includes('network')) {
              errorMessage = AUTH_ERRORS.network;
            }
            
            setError(errorMessage);
            setLoading(false);
            reject(err);
          },
          newPasswordRequired: (userAttributes, requiredAttributes) => {
            // Handle new password required challenge
            setLoading(false);
            resolve({ 
              requiresNewPassword: true, 
              userAttributes, 
              requiredAttributes,
              cognitoUser 
            });
          }
        });
      });
    } catch (err) {
      console.error('Login process error:', err);
      setError(AUTH_ERRORS.generalError);
      setLoading(false);
      throw err;
    }
  }, [storeTokens, initAwsClients]);

  // Request a password reset
  const requestPasswordReset = useCallback((username) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool
      });
      
      cognitoUser.forgotPassword({
        onSuccess: (data) => {
          console.log('Password reset request successful:', data);
          resolve({ success: true });
        },
        onFailure: (err) => {
          console.error('Detailed error in password reset:', err);
          reject(err);
        }
      });
    });
  }, []);

  // Confirm password reset with verification code
  const confirmPasswordReset = useCallback((username, verificationCode, newPassword) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool
      });
      
      cognitoUser.confirmPassword(verificationCode, newPassword, {
        onSuccess: () => {
          resolve({ success: true });
        },
        onFailure: (err) => {
          console.error('Error confirming password reset:', err);
          reject(err);
        }
      });
    });
  }, []);

  // Update user attributes
  const updateUserAttributes = useCallback((attributes) => {
    return new Promise((resolve, reject) => {
      const currentUser = userPool.getCurrentUser();
      
      if (!currentUser) {
        reject(new Error('No authenticated user'));
        return;
      }
      
      currentUser.getSession((err, session) => {
        if (err) {
          reject(err);
          return;
        }
        
        const attributeList = Object.entries(attributes).map(
          ([name, value]) => new CognitoUserAttribute({ Name: name, Value: value })
        );
        
        currentUser.updateAttributes(attributeList, (err, result) => {
          if (err) {
            reject(err);
            return;
          }
          
          resolve(result);
        });
      });
    });
  }, []);

  // Track user activity to manage session timeout
  const trackActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Check for user inactivity and handle session timeout
  const checkInactivity = useCallback(() => {
    const currentTime = Date.now();
    const inactiveTime = currentTime - lastActivityRef.current;
    
    if (inactiveTime >= SESSION_CONFIG.sessionInactivityTimeout) {
      console.log('User inactive for too long, logging out.');
      logout();
    } else {
      // Schedule next check
      inactivityTimerRef.current = setTimeout(
        checkInactivity, 
        Math.min(SESSION_CONFIG.sessionInactivityTimeout - inactiveTime, 60000) // Check every minute or less
      );
    }
  }, [logout]);

  // Set up session warning before expiration
  const setSessionWarning = useCallback(() => {
    if (!sessionExpiry) return;
    
    const timeToExpiry = sessionExpiry - Date.now();
    
    if (timeToExpiry <= 0) {
      // Session expired
      logout();
      return;
    }
    
    if (timeToExpiry <= SESSION_CONFIG.sessionTimeoutWarning) {
      // Show warning
      setShowSessionWarning(true);
    } else {
      // Schedule warning
      const warningTime = timeToExpiry - SESSION_CONFIG.sessionTimeoutWarning;
      sessionWarningTimerRef.current = setTimeout(() => {
        setShowSessionWarning(true);
      }, warningTime);
    }
  }, [sessionExpiry, logout]);

  // Load initial user session
  useEffect(() => {
    const loadUserSession = async () => {
      setLoading(true);
      
      try {
        const currentUser = userPool.getCurrentUser();
        
        if (!currentUser) {
          setTokenValidationComplete(true);
          setLoading(false);
          return;
        }
        
        // Get session from Cognito
        currentUser.getSession(async (err, session) => {
          if (err || !session || !session.isValid()) {
            console.log('Invalid or expired session');
            clearTokens();
            setTokenValidationComplete(true);
            setLoading(false);
            return;
          }
          
          // Valid session found
          const tokens = {
            accessToken: session.getAccessToken().getJwtToken(),
            idToken: session.getIdToken().getJwtToken(),
            refreshToken: session.getRefreshToken().getToken()
          };
          
          // Use the improved token storage
          storeTokens(tokens);
          
          const userData = extractUserFromToken(tokens.idToken);
          setUser(userData);
          setIsAuthenticated(true);
          
          // Set session expiry
          const decodedToken = jwtDecode(tokens.idToken);
          const expiryTime = decodedToken.exp * 1000; // Convert to milliseconds
          setSessionExpiry(expiryTime);
          
          // Initialize AWS clients
          await initAwsClients();
          
          setTokenValidationComplete(true);
          setLoading(false);
        });
      } catch (err) {
        console.error('Error loading user session:', err);
        setError(AUTH_ERRORS.generalError);
        setTokenValidationComplete(true);
        setLoading(false);
      }
    };
    
    loadUserSession();
    
    // Set up event listeners for activity tracking
    const activityEvents = ['mousedown', 'keypress', 'scroll', 'touchstart'];
    activityEvents.forEach(event => window.addEventListener(event, trackActivity));
    
    // Start inactivity timer
    inactivityTimerRef.current = setTimeout(checkInactivity, SESSION_CONFIG.sessionInactivityTimeout);
    
    return () => {
      // Clean up event listeners and timers
      activityEvents.forEach(event => window.removeEventListener(event, trackActivity));
      
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (sessionWarningTimerRef.current) clearTimeout(sessionWarningTimerRef.current);
    };
  }, []); // Empty dependency array since this should only run once on mount

  // Set up token refresh interval
  useEffect(() => {
    if (!isAuthenticated || !sessionExpiry) return;
    
    const setupRefreshTimer = () => {
      const timeToExpiry = sessionExpiry - Date.now();
      const refreshTime = Math.max(timeToExpiry - SESSION_CONFIG.tokenRefreshBuffer, 0);

      
      // Clear any existing timer
      if (refreshTimerRef.current) {
        console.log('Clearing existing refresh timer');
        clearTimeout(refreshTimerRef.current);
      }
      
      console.log(`[DEBUG REFRESH] Next token refresh scheduled in ${Math.round(refreshTime/60000)} minutes | sessionExpiry: ${new Date(sessionExpiry).toISOString()} | current time: ${new Date().toISOString()} | buffer: ${Math.round(refreshTime/60000)} minutes`);
      
      // Only set a new timer if there's significant time remaining
      if (refreshTime > 30000) { // Only schedule if more than 30 seconds until refresh
        console.log(`[DEBUG REFRESH] Setting timeout for ${new Date(Date.now() + refreshTime).toISOString()}`);
        refreshTimerRef.current = setTimeout(async () => {
          try {
            console.log(`[DEBUG REFRESH] Refresh timer triggered at ${new Date().toISOString()}`);
            console.log('Refreshing tokens before expiry');
            // Get new tokens and pass directly to initAwsClients
            const newTokens = await refreshTokens({ 
              forceRefresh: true,
              source: 'refreshTimer'  // Add source for debugging
            });
            
            if (newTokens) {
              // Initialize AWS clients with the fresh token if needed
              await initAwsClients(newTokens.idToken);
            }
            
            // Don't immediately call setupRefreshTimer - it will be called when session expiry is updated
          } catch (err) {
            console.error('Failed to refresh tokens:', err);
            if (retryCounterRef.current < SESSION_CONFIG.maxRetryAttempts) {
              // Retry with exponential backoff
              retryCounterRef.current++;
              const delay = SESSION_CONFIG.retryDelayMs * Math.pow(2, retryCounterRef.current - 1);
              setTimeout(async () => {
                try {
                  const newTokens = await refreshTokens({ forceRefresh: true });
                  if (newTokens) {
                    await initAwsClients(newTokens.idToken);
                  }
                  retryCounterRef.current = 0;
                } catch (retryErr) {
                  console.error('Failed to refresh tokens after retry:', retryErr);
                  // If we've exhausted retries, log out
                  if (retryCounterRef.current >= SESSION_CONFIG.maxRetryAttempts) {
                    logout();
                  }
                }
              }, delay);
            } else {
              // Exhausted retries, log out
              logout();
            }
          }
        }, refreshTime);
      } else {
        console.log('[DEBUG REFRESH] Token refresh time too short, skipping refresh timer setup');
      }
    };
    
    console.log(`[DEBUG REFRESH] Setting up refresh timer, sessionExpiry changed to: ${sessionExpiry}`);
    setupRefreshTimer();
    setSessionWarning();
    
    return () => {
      console.log('[DEBUG REFRESH] Cleanup running for token refresh effect');
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      if (sessionWarningTimerRef.current) clearTimeout(sessionWarningTimerRef.current);
    };
  }, [isAuthenticated, sessionExpiry, refreshTokens, logout, setSessionWarning]);

  // Sync across browser tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      // Only respond to relevant storage changes
      if (SESSION_CONFIG.storageType === 'localStorage' && 
         ['accessToken', 'idToken', 'refreshToken'].includes(e.key)) {
        if (!e.newValue && e.oldValue) {
          // Token was removed in another tab, log out here too
          logout();
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [logout]);

  // Run AWS client initialization when authentication state is confirmed
  useEffect(() => {
    if (tokenValidationComplete && isAuthenticated) {
      initAwsClients().then(() => setAwsReady(true));
    }
  }, [tokenValidationComplete, isAuthenticated, initAwsClients]);

  // Register our refreshTokens function with the auth utility
  useEffect(() => {
    // Import lazily to avoid circular dependency
    import('../../utils/awsAuth').then(({ registerRefreshTokensFunction }) => {
      registerRefreshTokensFunction(refreshTokens);
    }).catch(err => {
      console.error('Failed to register refresh tokens function', err);
    });
  }, [refreshTokens]);

  // Provide the authentication context to children with additional methods from the example
  const contextValue = {
    user,
    isAuthenticated,
    loading,
    error,
    tokenValidationComplete,
    sessionExpiry,
    showSessionWarning,
    keepMeLoggedIn,
    setKeepMeLoggedIn,
    awsReady, // Expose AWS Ready state
    login,
    logout,
    refreshTokens,
    requestPasswordReset,
    confirmPasswordReset,
    updateUserAttributes,
    // AWS clients
    bedrockRuntimeClient: bedrockRuntimeClientRef.current,
    dynamoDBClient: dynamoDBClientRef.current,
    s3Client: s3ClientRef.current,
    // Client accessor methods
    getBedrockRuntimeClient,
    getDynamoDBClient,
    getS3Client,
    getTemporaryCredentials,
    getStoredTokens, // Expose token retrieval method
    isTokenExpiring,  // Expose token expiry check
  };
  
  // Update global reference for non-component code to access
  if (window._authContextRef) {
    window._authContextRef.current = contextValue;
  }
  
  // Make auth context available globally for utility functions
  useEffect(() => {
    window.authContext = {
      user,
      isAuthenticated,
      getTemporaryCredentials,
      getBedrockRuntimeClient,
      getDynamoDBClient,
      getS3Client
    };
    
    return () => {
      // Cleanup on unmount
      delete window.authContext;
    };
  }, [user, isAuthenticated, getTemporaryCredentials, getBedrockRuntimeClient, getDynamoDBClient, getS3Client]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);